<template>
  <q-dialog v-model="isOpen" persistent maximized transition-show="slide-up" transition-hide="slide-down">
    <q-card class="video-generator-dialog">
      <VideoGeneratorTopBar :image="image" :hasImage="hasImage" @update:isOpen="$emit('update:isOpen', $event)" />
      <div class="generator-container">
        <!-- Left Section - Image Preview and Settings -->
        <div class="left-section">
          <!-- AI Video Generator Header -->
          <div class="section-header">
            <h2 class="section-title">AI Video Generator</h2>
            <p class="section-description">Transform your images into stunning videos with AI</p>
          </div>

          <!-- Upload Area (when no image preview is available) -->
          <div v-if="!hasImagePreview" class="upload-section">
            <div class="upload-area" :class="{ 'drag-over': isDragging }" @dragover.prevent="onDragOver" @dragleave.prevent="onDragLeave" @drop.prevent="onDrop" @click="triggerFileInput">
              <input type="file" ref="fileInput" @change="onFileSelected" accept="image/jpeg,image/jpg,image/webp" class="hidden-input" />

              <div class="upload-placeholder">
                <div class="upload-icon-container">
                  <q-icon name="cloud_upload" size="30px" color="grey-5" />
                </div>
                <div class="upload-text">
                  <h3 class="upload-main-text">Drop your image here</h3>
                  <p class="upload-sub-text">or click to browse files</p>
                </div>
                <div class="upload-formats">Supported formats: JPEG, WebP</div>
              </div>
            </div>
          </div>

          <!-- Image Preview (when image is available) -->
          <div v-else>
            <div class="image-preview-section">
              <q-img :src="currentImage.optimized_url || currentImage.url || currentImage.preview" :placeholder-src="currentImage.preview_url" class="selected-image" fit="contain" />
            </div>

            <!-- Upload Button Section (below image preview when not uploaded) -->
            <div v-if="uploadedImage && !uploadedImage.isUploaded" class="upload-action-section">
              <q-btn class="upload-action-button" color="primary" @click="uploadSelectedImage" :loading="$store.getters['uploadProgress/hasActiveUploads']" no-caps unelevated>
                <template v-if="!$store.getters['uploadProgress/hasActiveUploads']">
                  <q-icon name="cloud_upload" size="16px" class="q-mr-sm" />
                  Upload Image
                </template>
                <template v-slot:loading>
                  <q-spinner color="white" class="on-left" />
                  Uploading...
                </template>
              </q-btn>
            </div>
          </div>

          <!-- Camera Movement Selection -->
          <div v-if="hasImage" class="camera-movement-section">
            <div class="movement-header">
              <label class="movement-caption">Choose a camera effect below:</label>
            </div>

            <!-- Camera Movement Grid -->
            <div class="movement-grid">
              <div
                v-for="movement in klingMovements"
                :key="movement.value"
                class="movement-card"
                :class="{
                  'movement-card--selected': selectedLabel === movement.value,
                  'movement-card--disabled': isSelectionDisabled
                }"
                @click="!isSelectionDisabled && selectMovement(movement.value)"
                @mouseenter="showMovementTooltip(movement.value, $event)"
                @mouseleave="hideMovementTooltip"
              >
                <div class="movement-icon">
                  <q-icon :name="movement.icon" size="24px" />
                </div>
                <div class="movement-label">{{ movement.label }}</div>
              </div>
            </div>
          </div>

          <!-- Helper Text -->
          <div class="helper-text-container">
            <div v-if="!hasImagePreview" class="helper-text helper-text--warning">
              <q-icon name="upload" size="18px" class="helper-icon" />
              <span class="helper-message">Upload an image to get started</span>
            </div>
            <div v-else-if="hasImagePreview && !hasImage" class="helper-text helper-text--warning">
              <q-icon name="cloud_upload" size="18px" class="helper-icon" />
              <span class="helper-message">Click upload button to continue</span>
            </div>
            <div v-else-if="isSelectionDisabled" class="helper-text helper-text--info">
              <q-icon name="hourglass_empty" size="18px" class="helper-icon" />
              <span class="helper-message">Please wait for current video to complete</span>
            </div>
            <div v-else-if="!selectedLabel" class="helper-text helper-text--warning">
              <q-icon name="videocam" size="18px" class="helper-icon" />
              <span class="helper-message">Choose a camera movement</span>
            </div>
            <div v-else class="helper-text helper-text--success">
              <q-icon name="check_circle" size="18px" class="helper-icon" />
              <span class="helper-message">Ready to generate your video!</span>
            </div>
          </div>

          <!-- Generate Button -->
          <div v-if="hasImage" class="action-section">
            <q-btn class="action-button action-button--primary" :loading="isGenerating" :disable="isSelectionDisabled || !hasImage || !selectedLabel" @click="generateVideo" flat no-caps>
              <template v-if="!isGenerating">
                <q-icon name="movie" size="xs" class="button-icon" color="grey-7" />
                Generate Video
              </template>
              <template v-slot:loading>
                <q-spinner color="grey-7" class="on-left" />
                {{ getLoadingText }}
              </template>
            </q-btn>
          </div>
        </div>

        <!-- Right Section - Generated Videos Grid -->
        <div class="right-section">
          <!-- Generated Videos Header -->
          <div class="section-header-generator">
            <h2 class="section-title">Generated Videos</h2>
            <div class="badge">AI Generated</div>
          </div>

          <!-- Videos Grid -->
          <VideoGrid
            :video-history="videoHistory"
            :has-any-processing-item="hasAnyProcessingItem"
            :current-processing-item="currentProcessingItem"
            :show-create-placeholder="showCreatePlaceholder"
            :is-generating="isGenerating"
            @play="playVideo"
            @download="downloadVideo"
            @create-new="createNewClickHandler"
          />
        </div>
      </div>

      <!-- Video Tooltip -->
      <VideoTooltip :movement-id="hoveredMovement" :name="hoveredMovement" :description="getMovementDescription(hoveredMovement)" :position-style="tooltipStyles" />
    </q-card>
  </q-dialog>
</template>

<script>
import { ReplicateApi } from '@api/index';
import VideoGeneratorTopBar from '@components/video-generator/VideoGeneratorTopBar.vue';
import VideoGrid from '@components/video-generator/videogrid/VideoGrid.vue';
import VideoTooltip from '@components/video-generator/VideoTooltip.vue';
import { useQueryClient } from '@tanstack/vue-query';
import fileUpload from '@/utils/fileUpload';
import { createProgressCallback } from '@/utils/uploadHelpers';

export default {
  name: 'VideoGenerator',
  components: {
    VideoGeneratorTopBar,
    VideoGrid,
    VideoTooltip
  },
  props: {
    image: {
      type: Object,
      required: false,
      default: null
    },
    isOpen: {
      type: Boolean,
      required: true
    }
  },
  setup() {
    const queryClient = useQueryClient();
    return { queryClient };
  },
  data() {
    return {
      selectedLabel: '',
      isGenerating: false,
      uploadedImage: null,
      isDragging: false,
      currentPredictionId: null,
      // Tooltip functionality
      hoveredMovement: null,
      tooltipStyles: {
        top: '0px',
        left: '0px'
      },
      klingMovements: [
        { label: 'Turntable 360', value: 'Turntable 360', icon: 'refresh' },
        { label: 'Hero Focus', value: 'Hero Focus', icon: 'center_focus_strong' },
        { label: 'Smooth Entry', value: 'Smooth Entry', icon: 'trending_up' },
        { label: 'Gimbal Glide', value: 'Gimbal Glide', icon: 'photo_camera' },
        { label: 'Portrait Pan', value: 'Portrait Pan', icon: 'person' },
        { label: 'Real Look', value: 'Real Look', icon: 'visibility' }
      ]
    };
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeydown);
    this.$nextTick(() => {
      this.loadVideoHistory();
    });
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown);
    this.cleanupGeneration();
    this.$store.dispatch('videoHistory/clearVideoHistory');
  },
  computed: {
    currentImage() {
      return this.uploadedImage || this.image;
    },
    hasImage() {
      return !!(this.currentImage && (this.currentImage.url || this.currentImage.optimized_url) && (this.currentImage.id || this.currentImage.content_id));
    },
    hasImagePreview() {
      return !!(this.currentImage && (this.currentImage.url || this.currentImage.optimized_url));
    },

    // Simplified video history - removed complex processing logic
    videoHistory() {
      const historyData = this.$store.getters['videoHistory/getVideoHistoryData'];
      if (!historyData?.histories) return [];

      const isStandaloneMode = !this.hasImage;
      const fifteenMinutesAgo = Date.now() - 15 * 60 * 1000;

      return historyData.histories
        .map(history => ({
          id: history.job_id,
          url: history.result,
          label: history.camera_effect || 'AI Generated',
          timestamp: history.created_at,
          status: history.job_status,
          hasError: history.job_status === 'failed' || history.job_status === 'error' || (!history.result && history.job_status === 'succeeded'),
          isProcessing: history.job_status === 'processing' || history.job_status === 'starting'
        }))
        .filter(item => {
          if (isStandaloneMode) {
            // In standalone mode, mark old processing items as stuck
            if (item.isProcessing && new Date(item.timestamp).getTime() < fifteenMinutesAgo) {
              item.hasError = true;
              item.status = 'stuck';
              item.isProcessing = false;
            }
            return true;
          }
          // In specific image mode, hide processing items (shown in main placeholder)
          return !item.isProcessing;
        })
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    },

    // Simplified processing item detection
    hasAnyProcessingItem() {
      if (this.isGenerating) return true;

      if (!this.hasImage) {
        // Standalone mode - check global store
        const activeGenerations = this.$store.getters['videoGenerationProgress/activeGenerations'] || [];
        return activeGenerations.some(gen => gen.progress < 100 && gen.status !== 'error');
      }

      // Specific image mode - check for recent processing items
      const historyData = this.$store.getters['videoHistory/getVideoHistoryData'];
      if (!historyData?.histories) return false;

      const fifteenMinutesAgo = Date.now() - 15 * 60 * 1000;
      return historyData.histories.some(history => (history.job_status === 'processing' || history.job_status === 'starting') && new Date(history.created_at).getTime() >= fifteenMinutesAgo);
    },

    // Simplified current processing item
    currentProcessingItem() {
      if (this.isGenerating && this.currentPredictionId) {
        // Find the actual generation progress from the store
        const activeGenerations = this.$store.getters['videoGenerationProgress/activeGenerations'] || [];
        const currentGeneration = activeGenerations.find(gen => gen.predictionId === this.currentPredictionId);

        return {
          progress: currentGeneration?.progress || 0,
          cameraEffect: this.selectedLabel,
          timeRemaining: currentGeneration?.message || 'Generating video...'
        };
      }

      if (!this.hasImage) {
        // Standalone mode
        const activeGenerations = this.$store.getters['videoGenerationProgress/activeGenerations'] || [];
        const mostRecent = activeGenerations.filter(gen => gen.progress < 100 && gen.status !== 'error').sort((a, b) => new Date(b.startTime) - new Date(a.startTime))[0];

        return mostRecent
          ? {
              progress: mostRecent.progress || 0,
              cameraEffect: mostRecent.cameraEffect || 'AI Generated',
              timeRemaining: 'Processing...'
            }
          : null;
      }

      // Specific image mode - find most recent processing item
      const historyData = this.$store.getters['videoHistory/getVideoHistoryData'];
      if (!historyData?.histories) return null;

      const fifteenMinutesAgo = Date.now() - 15 * 60 * 1000;
      const recentProcessing = historyData.histories
        .filter(history => (history.job_status === 'processing' || history.job_status === 'starting') && new Date(history.created_at).getTime() >= fifteenMinutesAgo)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];

      if (!recentProcessing) return null;

      const activeGenerations = this.$store.getters['videoGenerationProgress/activeGenerations'] || [];
      const generation = activeGenerations.find(gen => gen.predictionId === recentProcessing.job_id);

      return {
        progress: generation?.progress || 0,
        cameraEffect: recentProcessing.camera_effect || 'AI Generated',
        timeRemaining: 'Processing...'
      };
    },

    isSelectionDisabled() {
      return this.hasAnyProcessingItem;
    },
    showCreatePlaceholder() {
      return !this.isGenerating && (this.videoHistory.length > 0 || this.hasImage);
    },
    getLoadingText() {
      return 'AI At Work… ✨';
    }
  },
  watch: {
    currentImage: {
      handler(newImage, oldImage) {
        const shouldReload = (newImage && newImage.id && (!oldImage || newImage.id !== oldImage.id)) || (!newImage && oldImage && oldImage.id) || (newImage && !oldImage);
        if (shouldReload) {
          this.loadVideoHistory();
        }
      },
      immediate: false
    },
    isOpen: {
      handler(newIsOpen, oldIsOpen) {
        if (newIsOpen && !oldIsOpen) {
          const historyData = this.$store.getters['videoHistory/getVideoHistoryData'];
          if (!historyData) {
            this.loadVideoHistory();
          }
        }
      },
      immediate: false
    }
  },
  methods: {
    // Event handlers
    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.$emit('update:isOpen', false);
      }
    },
    createNewClickHandler() {
      if (this.selectedLabel) {
        this.generateVideo();
      }
    },
    selectMovement(movementValue) {
      this.selectedLabel = this.selectedLabel === movementValue ? '' : movementValue;
    },

    // Simplified notification helper
    notify(type, message) {
      this.$q.notify({
        type,
        message,
        position: 'top',
        timeout: type === 'info' ? 2000 : 3000
      });
    },

    // File upload handlers
    onDragOver(event) {
      event.preventDefault();
      this.isDragging = true;
    },
    onDragLeave(event) {
      event.preventDefault();
      this.isDragging = false;
    },
    onDrop(event) {
      event.preventDefault();
      this.isDragging = false;
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.processFile(files[0]);
      }
    },
    triggerFileInput() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click();
      }
    },
    onFileSelected(event) {
      const file = event.target.files[0];
      if (file) {
        this.processFile(file);
      }
      event.target.value = '';
    },
    processFile(file) {
      // Validate file
      const supportedTypes = ['image/jpeg', 'image/jpg', 'image/webp'];
      if (!supportedTypes.includes(file.type)) {
        return this.notify('negative', 'Please select a valid image file (JPEG or WebP)');
      }

      if (file.size > 50 * 1024 * 1024) {
        // 50MB
        return this.notify('negative', 'File size must be less than 50MB');
      }

      // Create preview
      const imageUrl = URL.createObjectURL(file);
      this.uploadedImage = {
        file,
        url: imageUrl,
        optimized_url: imageUrl,
        preview_url: imageUrl,
        name: file.name,
        isUploaded: false
      };
    },

    // Upload image
    async uploadSelectedImage() {
      if (!this.uploadedImage || !this.uploadedImage.file) return;

      try {
        this.notify('info', 'Uploading image...');

        const globalUploads = await this.$store.dispatch('uploadProgress/addUploads', [this.uploadedImage.file]);
        const globalUpload = globalUploads[0];
        this.$store.dispatch('uploadProgress/showDialog');

        await this.$store.dispatch('uploadProgress/updateUpload', {
          id: globalUpload.id,
          data: { status: 'uploading' }
        });

        const uploadedContent = await fileUpload.uploadFile(this.uploadedImage.file, createProgressCallback(this.$store, globalUpload.id), globalUpload.id);

        if (!uploadedContent) {
          throw new Error('Upload failed - no content returned');
        }

        this.$amplitude.trackEvent('Storage Used', {
          size: this.uploadedImage.file.size
        });

        await this.$store.dispatch('uploadProgress/updateUploadWithRealId', {
          tempId: globalUpload.id,
          realId: uploadedContent.id,
          data: {
            canDelete: true,
            contentType: uploadedContent.content_type || 'image'
          }
        });

        // Clean up preview URL
        if (this.uploadedImage.url) {
          URL.revokeObjectURL(this.uploadedImage.url);
        }

        // Update with uploaded content
        this.uploadedImage = {
          id: uploadedContent.id,
          content_id: uploadedContent.id,
          url: uploadedContent.url,
          optimized_url: uploadedContent.optimized_url || uploadedContent.url,
          preview_url: uploadedContent.preview_url,
          name: uploadedContent.name || this.uploadedImage.name,
          isUploaded: true
        };

        this.notify('positive', 'Image uploaded successfully!');

        // Refresh gallery
        this.queryClient?.invalidateQueries({ queryKey: ['content'], exact: false });
        this.queryClient?.invalidateQueries({ queryKey: ['collections'] });

        // Dispatch event for gallery refresh
        window.dispatchEvent?.(
          new CustomEvent('content-uploaded', {
            detail: { contentId: uploadedContent.id, contentType: 'image' }
          })
        );

        this.loadVideoHistory();
      } catch (error) {
        console.error('Error uploading image:', error);
        this.notify('negative', 'Failed to upload image. Please try again.');
      }
    },

    // Video generation
    async generateVideo() {
      if (!this.selectedLabel) {
        return this.notify('negative', 'Please select a camera movement type');
      }

      if (!this.currentImage) {
        return this.notify('negative', 'Invalid image data. Please try again.');
      }

      this.isGenerating = true;

      try {
        // Prepare image data
        const params = {
          label: this.selectedLabel,
          content_id: this.currentImage.id || this.currentImage.content_id
        };

        if (this.currentImage.file) {
          params.image = this.currentImage.file;
        } else {
          const response = await fetch(this.currentImage.optimized_url || this.currentImage.url);
          params.image = new Blob([await response.blob()], { type: 'image/webp' });
        }

        // Generate video
        const response = await ReplicateApi.generateVideoWithKling(params);
        const predictionId = response.data?.prediction_id;

        if (!predictionId) {
          throw new Error('No prediction ID returned');
        }

        this.currentPredictionId = predictionId;

        // Start tracking
        this.$store.dispatch('videoGenerationProgress/startVideoGeneration', {
          predictionId,
          imageUrl: this.currentImage.optimized_url || this.currentImage.url,
          cameraEffect: this.selectedLabel,
          fileName: this.getImageName()
        });

        // Update status to processing after successful API call
        this.$store.dispatch('videoGenerationProgress/updateGeneration', {
          id: predictionId,
          data: {
            status: 'processing',
            message: 'Generating video...'
          }
        });

        // Show the generation progress dialog
        this.$store.dispatch('videoGenerationProgress/showDialog');

        this.queryClient?.invalidateQueries({ queryKey: ['video-generation-status'] });
        this.$amplitude.trackEvent('Prompt Preset Used (Video)', {
          predictionId,
          cameraEffect: this.selectedLabel,
          contentId: this.currentImage.id || this.currentImage.content_id
        });
        this.pollPredictionStatus(predictionId);
      } catch (error) {
        console.error('Error generating video:', error);
        this.notify('negative', 'Failed to generate video. Please try again.');
        this.handleGenerationFailure('An error occurred. Please try again.');
      }
    },

    // Simplified polling
    pollPredictionStatus(predictionId) {
      const maxRetries = 60;
      let retryCount = 0;

      const checkStatus = () => {
        if (retryCount >= maxRetries) {
          this.handleGenerationFailure('Generation timed out. Please try again.');
          return;
        }

        ReplicateApi.getPredictionStatus(predictionId)
          .then(response => {
            const { prediction } = response.data;
            const status = prediction?.status;
            const output = prediction?.output;

            if (status === 'succeeded' && output) {
              this.handleGenerationSuccess(output);
            } else if (status === 'failed') {
              this.handleGenerationFailure('Generation failed. Please try again.');
            } else {
              retryCount++;
              setTimeout(checkStatus, 5000);
            }
          })
          .catch(error => {
            console.error('Error checking prediction status:', error);
            retryCount++;
            if (retryCount >= maxRetries) {
              this.handleGenerationFailure('Connection error. Please try again.');
            } else {
              setTimeout(checkStatus, 5000);
            }
          });
      };

      checkStatus();
    },

    handleGenerationSuccess(videoUrl) {
      this.$store.dispatch('videoGenerationProgress/handleGenerationCompletion', {
        id: this.currentPredictionId,
        videoUrl,
        message: 'Video generated successfully!',
        isActivelyManaged: false
      });

      this.loadVideoHistory();
      this.notify('positive', 'Video generated successfully!');
      this.resetGeneration();

      this.$amplitude.trackEvent('Video Generated', {
        predictionId: this.currentPredictionId,
        cameraEffect: this.selectedLabel,
        contentId: this?.currentImage?.id || this?.currentImage?.content_id
      });
    },

    handleGenerationFailure(message) {
      if (this.currentPredictionId) {
        this.$store.dispatch('videoGenerationProgress/failGeneration', {
          id: this.currentPredictionId,
          message
        });
      }
      this.resetGeneration();
    },

    resetGeneration() {
      this.isGenerating = false;
      this.currentPredictionId = null;
    },

    // Simplified video history loading
    async loadVideoHistory() {
      try {
        const contentId = this.hasImage ? this.currentImage.id || this.currentImage.content_id : 0;
        const historyData = await this.$store.dispatch('videoHistory/loadVideoHistory', {
          contentId,
          contentType: 'image'
        });

        // Restore active processing items to global progress (simplified)
        if (historyData?.histories && this.hasImage) {
          const fifteenMinutesAgo = Date.now() - 15 * 60 * 1000;
          const recentProcessingItems = historyData.histories.filter(
            history => (history.job_status === 'processing' || history.job_status === 'starting') && new Date(history.created_at).getTime() >= fifteenMinutesAgo
          );

          recentProcessingItems.forEach(item => {
            const elapsed = Date.now() - new Date(item.created_at).getTime();
            const progress = Math.min((elapsed / 240000) * 100, 95); // 4 minutes max

            this.$store.dispatch('videoGenerationProgress/addGeneration', {
              id: item.job_id,
              predictionId: item.job_id,
              imageUrl: this.currentImage.optimized_url || this.currentImage.url,
              cameraEffect: item.camera_effect || 'AI Generated',
              fileName: this.getImageName(),
              progress: Math.max(0, progress),
              status: item.job_status,
              startTime: item.created_at,
              progressStartTime: new Date(item.created_at).getTime(),
              message: 'Generating video...',
              videoUrl: item.result || null,
              isRestored: true
            });
          });

          if (recentProcessingItems.length > 0) {
            this.$store.dispatch('videoGenerationProgress/ensureProgressTimer');
            this.$store.dispatch('videoGenerationProgress/showDialog');
          }
        }
      } catch (error) {
        console.error('Error loading video history:', error);
      }
    },

    // Video playback
    playVideo(item, event) {
      if (item.hasError) {
        return this.notify('negative', 'This video failed to load and cannot be played');
      }

      const videoElement = event?.target?.closest('.video-item')?.querySelector('video');
      if (videoElement) {
        if (videoElement.paused) {
          videoElement.play().catch(error => {
            console.error('Error playing video:', error);
            this.$set(item, 'hasError', true);
          });
        } else {
          videoElement.pause();
        }
      }
    },

    // Download video
    async downloadVideo(videoUrl) {
      try {
        this.notify('info', 'Preparing video for download...');

        const response = await fetch(videoUrl);
        if (!response.ok) throw new Error('Failed to fetch video');

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `video-${this.selectedLabel.replace(/\s+/g, '-').toLowerCase()}.mp4`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();

        setTimeout(() => {
          document.body.removeChild(link);
          URL.revokeObjectURL(blobUrl);
        }, 100);

        this.notify('positive', 'Video downloaded successfully');
      } catch (error) {
        console.error('Download failed:', error);
        this.notify('negative', 'Failed to download video');
      }
    },

    // Cleanup
    cleanupGeneration() {
      if (this.currentPredictionId && this.isGenerating) {
        this.$store.dispatch('videoGenerationProgress/updateProgressForGeneration', {
          id: this.currentPredictionId,
          progress: 50,
          elapsed: 0,
          isActivelyManaged: false
        });
        this.$store.dispatch('videoGenerationProgress/ensureProgressTimer');
      }

      if (this.uploadedImage?.url) {
        URL.revokeObjectURL(this.uploadedImage.url);
      }
    },

    getImageName() {
      if (this.uploadedImage?.name) return this.uploadedImage.name;
      if (this.image?.name) return this.image.name;
      if (this.image?.url) {
        try {
          let filename = this.image.url.substring(this.image.url.lastIndexOf('/') + 1);
          if (filename.includes('?')) filename = filename.split('?')[0];
          return decodeURIComponent(filename) || 'Selected Image';
        } catch {
          return 'Selected Image';
        }
      }
      return 'Selected Image';
    },

    // Tooltip methods for camera movement cards
    showMovementTooltip(movementValue, event) {
      const cardRect = event.currentTarget.getBoundingClientRect();
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      const estimatedTooltipWidth = 350; // Max width of tooltip
      const estimatedTooltipHeight = 400; // Estimated height including animation

      // Calculate horizontal positioning
      let leftPosition = cardRect.right + 16; // Default: show to the right with 16px gap

      // If tooltip would overflow right edge, show to the left
      if (leftPosition + estimatedTooltipWidth > windowWidth - 20) {
        leftPosition = cardRect.left - estimatedTooltipWidth - 16;
      }

      // If still overflowing left edge, center it
      if (leftPosition < 20) {
        leftPosition = Math.max(20, (windowWidth - estimatedTooltipWidth) / 2);
      }

      // Calculate vertical positioning - center tooltip relative to the card
      let topPosition = cardRect.top + cardRect.height / 2 - estimatedTooltipHeight / 2;

      // Ensure tooltip doesn't go off screen
      if (topPosition < 20) {
        topPosition = 20; // Minimum padding from top
      } else if (topPosition + estimatedTooltipHeight > windowHeight - 20) {
        topPosition = windowHeight - estimatedTooltipHeight - 20; // Minimum padding from bottom
      }

      this.tooltipStyles = {
        top: `${topPosition}px`,
        left: `${leftPosition}px`
      };

      this.hoveredMovement = movementValue;
    },

    hideMovementTooltip() {
      this.hoveredMovement = null;
    },

    getMovementDescription(movementValue) {
      const descriptions = {
        'Turntable 360': 'Creates a smooth 360-degree rotation around your subject, perfect for showcasing products or creating dynamic reveals.',
        'Hero Focus': 'Maintains focus on the main subject while creating subtle camera movement that draws attention to key details.',
        'Smooth Entry': 'Generates an elegant camera approach that gradually reveals your subject with cinematic smoothness.',
        'Gimbal Glide': 'Mimics professional gimbal movement with fluid, stabilized motion that adds production value to any shot.',
        'Portrait Pan': 'Specially designed for portraits and people, creating gentle camera movement that enhances facial features.',
        'Real Look': 'Produces natural, realistic camera movement that feels authentic and unobtrusive to the viewer.'
      };
      return descriptions[movementValue] || '';
    }
  }
};
</script>

<style lang="scss" scoped>
.video-generator-dialog {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
}

.generator-container {
  background: var(--main-bg-color);
  height: 100vh;
  display: grid;
  width: 100%;
  grid-template-columns: 350px 1fr;
  gap: 16px;
  overflow: hidden;
  position: relative;
  padding: 16px 16px 16px 16px;
}

/* Left Section - Image Preview and Controls */
.left-section {
  background: var(--secondary-bg-color);
  padding: 16px;
  height: 100%;
  max-height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  box-shadow: 1px 1px 10px var(--card-shadow-color);
}

.section-header {
  margin: 8px 0 16px 0;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  flex-direction: column;
  padding: 0;
}

.section-title {
  @include mona-sans-font($size: 22px, $bolded: true, $color: var(--main-text-color), $weight: 700);
  margin: 0 0 6px 0;
  line-height: 1.4;
}

.section-description {
  @include inter-font($size: 14px, $bolded: false, $color: var(--secondary-text-color), $weight: 400);
  margin: 0;
  line-height: 1.4;
}

.upload-section {
  width: 100%;
  margin-bottom: 16px;
}

.upload-area {
  border: 2px dashed var(--grey-hr);
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: var(--secondary-bg-color);
  min-height: 160px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  &:hover {
    border-color: var(--border-outlined-btn-color);
    background-color: rgba(254, 203, 47, 0.02);
  }

  &.drag-over {
    border-color: var(--border-outlined-btn-color);
    background-color: rgba(254, 203, 47, 0.05);
    transform: scale(1.02);
  }
}

.hidden-input {
  display: none;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.upload-icon-container {
  margin-bottom: 16px;
  padding: 16px;
  border-radius: 50%;
  background-color: var(--main-bg-color);
}

.upload-text {
  margin-bottom: 16px;
  text-align: center;
}

.upload-main-text {
  @include mona-sans-font($size: 18px, $bolded: true, $color: var(--main-text-color), $weight: 600);
  margin: 0 0 8px 0;
  line-height: 1.3;
}

.upload-sub-text {
  @include inter-font($size: 14px, $color: var(--secondary-text-color), $weight: 400);
  margin: 0;
  line-height: 1.4;
}

.upload-formats {
  @include inter-font($size: 12px, $color: var(--secondary-text-color), $weight: 400);
  background-color: var(--main-bg-color);
  padding: 6px 12px;
  border-radius: 16px;
  display: inline-block;
}

.image-preview-section {
  width: 100%;
  height: 160px;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 12px;
  border: 1px solid var(--grey-hr);
}

.selected-image-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.image-preview-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 8px;
}

.selected-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  padding: 8px;
  border-radius: 8px;
  opacity: 0;
  transition: opacity 0.3s ease;

  .image-preview-wrapper:hover & {
    opacity: 1;
  }
}

.remove-btn {
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  padding: 0;
  width: 32px;
  height: 32px;
  min-height: 32px;
  min-width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);

  &:hover {
    background: rgba(244, 67, 54, 0.9);
    color: white;
  }

  :deep(.q-icon) {
    font-size: 16px;
  }
}

.image-info {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 8px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 0 0 8px 8px;
  color: white;
}

.image-name {
  @include inter-font($size: 12px, $bolded: true, $color: white, $weight: 500);
  margin: 0;
}

.image-size {
  @include inter-font($size: 10px, $bolded: false, $color: white, $weight: 400);
  margin-top: 4px;
}

.upload-action-section {
  margin-top: 12px;
}

.upload-action-button {
  width: 100%;
  height: 40px;
  border-radius: 8px;
  font-weight: 600;
  text-transform: none;
}

/* Camera Movement Section */
.camera-movement-section {
  margin-bottom: 16px;
}

.movement-header {
  margin-bottom: 16px;
}

.movement-caption {
  @include inter-font($size: 14px, $bolded: true, $color: var(--main-text-color), $weight: 600);
  margin-bottom: 4px;
  display: block;
}

.movement-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.movement-card {
  background: var(--main-bg-color);
  border: 2px solid var(--grey-hr);
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 80px;
  text-align: center;

  &:hover:not(.movement-card--disabled) {
    border-color: var(--border-outlined-btn-color);
    background-color: rgba(254, 203, 47, 0.05);
    transform: translateY(-2px);
  }

  &--selected {
    border-color: var(--border-outlined-btn-color);
    background-color: rgba(254, 203, 47, 0.1);

    .movement-icon {
      color: var(--border-outlined-btn-color);
    }
  }

  &--disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.movement-icon {
  margin-bottom: 8px;
  color: var(--secondary-text-color);
  transition: color 0.3s ease;
}

.movement-label {
  @include inter-font($size: 12px, $bolded: true, $color: var(--main-text-color), $weight: 600);
  line-height: 1.2;
}

.action-section {
  margin-top: 8px;
}

.action-button {
  width: 100%;
  height: 50px;
  padding: 12px 16px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  @include inter-font($size: 15px, $bolded: true, $color: var(--main-text-color), $weight: 600);
  display: flex;
  align-items: center;
  justify-content: center;

  &--primary {
    background: var(--main-btn-color);
    color: var(--main-text-color);

    &:hover {
      background: var(--main-btn-color);
      opacity: 0.9;
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.button-icon {
  margin-right: 8px;
  font-size: 16px;
}

.status-container {
  padding: 16px;
  background: var(--main-bg-color);
  border-radius: 8px;
}

/* Right Section - Generated Videos Grid */
.right-section {
  padding: 0 16px 16px 16px;
  background: var(--main-bg-color);
  border-radius: 10px;
}

.section-header-generator {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.badge {
  background: var(--main-bg-color);
  color: var(--secondary-text-color);
  padding: 4px 12px;
  border-radius: 16px;
  @include inter-font($size: 12px, $bolded: false, $color: var(--secondary-text-color), $weight: 500);
}

/* Helper Text Styling */
.helper-text-container {
  margin: 12px 0;
}

.helper-text {
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid;
  transition: all 0.2s ease;

  &--warning {
    background-color: rgba(#f39c12, 0.08);
    border-color: rgba(#f39c12, 0.2);

    .helper-icon {
      color: var(--warning);
    }

    .helper-message {
      color: var(--warning);
    }
  }

  &--success {
    background-color: rgba(#2ecc71, 0.08);
    border-color: rgba(#2ecc71, 0.2);

    .helper-icon {
      color: var(--positive);
    }

    .helper-message {
      color: var(--positive);
    }
  }

  &--info {
    background-color: rgba(#3498db, 0.08);
    border-color: rgba(#3498db, 0.2);

    .helper-icon {
      color: #3498db;
    }

    .helper-message {
      color: #3498db;
    }
  }
}

.helper-text-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.helper-icon {
  flex-shrink: 0;
  font-size: 18px;
}

.helper-message {
  @include inter-font($size: 14px, $bolded: false, $weight: 500);
  line-height: 1.4;
  margin: 0;
}
</style>
